// src/tabs/messages/segment-editor/direct-blocks.js — PARTIE B / B4 (ADR-272)
//
// Blocs conditionnels {#if …} PROPRES au parcours Direct, pour le menu
// « + Paragraphe conditionnel » de l'éditeur Direct (DirectMessagesView).
// MIROIR du mécanisme Plateformes (variable-labels.js : PLATFORM_BLOCK_OPTIONS /
// CONDITION_BLOCK_OPTIONS / BLOCK_MENU_GROUPS + ifEncartMeta), mais alimenté par
// le catalogue B2 (lib/direct-vars-catalog.js — DIRECT_VAR_CATALOG, entrées
// kind:'flag') AU LIEU des conditions Plateformes (is_airbnb/is_booking/
// caution_required…), qui n'ont AUCUN sens en Direct.
//
// ⚠️ SOURCE DE VÉRITÉ : les NOMS de conditions ci-dessous sont un miroir STATIQUE
// des drapeaux du catalogue B2 (comme TOKEN_GROUP dans DirectMessagesView). On ne
// les importe pas depuis lib/ pour ne PAS embarquer les dépendances serveur du
// catalogue (caution-mode / payment-derivation) dans le bundle client. Le
// garde-fou reste la validation serveur `unknownTokens` (par-slug + GLOBAL_VALID_VARS,
// qui contient DIRECT_CATALOG_VAR_NAMES) : une condition hors référentiel serait
// REFUSÉE à l'enregistrement. Ce fichier ne fait donc que PROPOSER — le serveur
// tranche. Tout drapeau ajouté au catalogue B2 doit être répercuté ici pour être
// insérable (sinon il reste tapable à la main, validé côté serveur).
//
// `is_*` (is_airbnb/is_booking/is_direct) sont VOLONTAIREMENT absents : une résa
// Direct est TOUJOURS « direct » — ces conditions plateforme n'ont pas de rôle ici.

import { parseIfExpression } from "../../../lib/message-segments";

// Palette AMBRE des encarts de condition — identique au bandeau générique/condition
// de l'éditeur Plateformes (variable-labels.js CONDITION_IF) : pas de couleur neuve.
const AMBER = { border: "#EF9F27", bg: "#FAEEDA", text: "#854F0B" };

// Métadonnées d'affichage d'un encart {#if name} côté Direct : bandeau explicite
// « SEULEMENT SI … » (miroir des libellés forts Plateformes) + palette ambre.
// Clé = nom technique du drapeau (catalogue B2). Un nom absent d'ici → null (le
// SegmentEditor retombe sur son ifEncartMeta générique « Si « … » est renseigné »).
export const DIRECT_IF_META = {
  // Caution (chemin argent — libellés alignés sur les drapeaux B2 canoniques)
  caution_empreinte: "SEULEMENT SI CAUTION EN EMPREINTE",
  caution_debit: "SEULEMENT SI CAUTION EN DÉBIT",
  caution_recue: "SEULEMENT SI CAUTION REÇUE",
  caution_manquante: "SEULEMENT SI CAUTION NON REÇUE",
  caution_deposee: "SEULEMENT SI CAUTION DÉPOSÉE",
  // Formulaire de bienvenue
  formulaire_rempli: "SEULEMENT SI FORMULAIRE REMPLI",
  formulaire_manquant: "SEULEMENT SI FORMULAIRE NON REMPLI",
  // Règles du logement
  animaux_non: "SEULEMENT SI ANIMAUX NON AUTORISÉS",
  fumeur_non: "SEULEMENT SI NON-FUMEUR",
  // Annulation & remboursement
  motif_affiche: "SEULEMENT SI MOTIF D'ANNULATION AFFICHÉ",
  remboursement_effectue: "SEULEMENT SI REMBOURSEMENT EFFECTUÉ",
  remboursement_total: "SEULEMENT SI REMBOURSEMENT TOTAL",
  remboursement_partiel: "SEULEMENT SI REMBOURSEMENT PARTIEL",
  aucun_remboursement: "SEULEMENT SI AUCUN REMBOURSEMENT",
  remboursement_aucun: "SEULEMENT SI AUCUN REMBOURSEMENT",
};

/**
 * Meta d'un encart {#if EXPR} Direct : bandeau « SEULEMENT SI … » ambre.
 *
 * B4-ext — le CUMUL est pris en charge : `{#if caution_manquante ET
 * caution_empreinte}` compose son bandeau à partir des libellés de CHAQUE
 * opérande (« SEULEMENT SI CAUTION NON REÇUE ET CAUTION EN EMPREINTE »). Le
 * SegmentEditor round-trippe désormais ces blocs — plus de bascule en lecture
 * seule (le moteur riche, lui, les évaluait déjà : evalIfExpression).
 *
 * Renvoie null si l'expression n'est pas exploitable ici — expression non
 * supportée par le moteur, ou UN SEUL opérande hors DIRECT_IF_META → le
 * SegmentEditor retombe sur son ifEncartMeta (générique / invalide), exactement
 * comme avant.
 * @param {string} expr nom de drapeau OU expression de cumul
 * @returns {{variant:'condition'|'cumul', label, border, bg, text:string}|null}
 */
export function directIfEncartMeta(expr) {
  const parsed = parseIfExpression(expr);
  if (!parsed.valid || parsed.operands.length === 0) return null;
  const labels = parsed.operands.map((op) => DIRECT_IF_META[op]);
  if (labels.some((l) => !l)) return null; // un opérande hors référentiel → fallback
  if (labels.length === 1)
    return { variant: "condition", label: labels[0], ...AMBER };
  const phrase = labels
    .map((l) => l.replace(/^SEULEMENT SI\s+/, ""))
    .join(` ${parsed.operator} `);
  return { variant: "cumul", label: `SEULEMENT SI ${phrase}`, ...AMBER };
}

// ── Menu GROUPÉ « + Paragraphe conditionnel » (Direct) ───────────────────────
// Même forme que BLOCK_MENU_GROUPS (Plateformes) : [{ category, options:[{name,label}] }].
// L'insertion réutilise insertBlock() du SegmentEditor (pose {#if name}…{/if}).
// Libellés courts « métier » (le bandeau de l'encart porte le « SEULEMENT SI … »).
export const DIRECT_CONDITION_BLOCK_GROUPS = [
  {
    category: "Caution",
    options: [
      { name: "caution_empreinte", label: "Caution en empreinte" },
      { name: "caution_debit", label: "Caution en débit" },
      { name: "caution_recue", label: "Caution reçue" },
      { name: "caution_manquante", label: "Caution non reçue" },
      { name: "caution_deposee", label: "Caution déposée" },
    ],
  },
  {
    category: "Formulaire de bienvenue",
    options: [
      { name: "formulaire_rempli", label: "Formulaire rempli" },
      { name: "formulaire_manquant", label: "Formulaire non rempli" },
    ],
  },
  {
    category: "Règles du logement",
    options: [
      { name: "animaux_non", label: "Animaux non autorisés" },
      { name: "fumeur_non", label: "Non-fumeur" },
    ],
  },
  {
    category: "Annulation & remboursement",
    options: [
      { name: "motif_affiche", label: "Motif d'annulation affiché" },
      { name: "remboursement_effectue", label: "Remboursement effectué" },
      { name: "remboursement_total", label: "Remboursement total" },
      { name: "remboursement_partiel", label: "Remboursement partiel" },
      { name: "aucun_remboursement", label: "Aucun remboursement" },
    ],
  },
];
